/**
 * PageHeaderComponent - Updated with integrated notification system
 *
 * This component now integrates with the following notification services:
 * - ToastNotificationService: For general application notifications
 * - ScanNotificationIntegrationService: For scan completion notifications
 * - ScanStatusMonitorService: For real-time scan status monitoring
 *
 * Features:
 * - Dynamic notification display from multiple sources
 * - Real-time scan completion notifications
 * - Desktop and in-app notification support
 * - Interactive notification management (dismiss, view details)
 * - Responsive design with improved UX
 * - Automatic notification cleanup on component destroy
 *
 * The hardcoded notifications have been replaced with dynamic notifications
 * that come from the actual notification services used throughout the application.
 */
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { User } from 'src/app//core/models/user';
import { UserService } from '@core/services/user.service';
import { ThemeService } from '@core/services/theme.service';
import { TenableChildAccount } from '@core/tenable/models/tenable-child-account';
import { BehaviorSubject, Subscription, interval } from 'rxjs';
import roleData from '@data/user_roles.json';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import {
  ToastNotificationService,
  ToastNotification,
} from '@core/services/toast-notification.service';
import { ScanNotificationIntegrationService } from '@core/services/scan-notification-integration.service';
import { SignalrRealtimeService } from '@core/services/signalr-realtime.service';
import {
  ScanStatusMonitorService,
  ScanStatusChange,
} from '@core/services/scan-status-monitor.service';
import { FileService } from '@core/services/file.service';
import { AlertNotificationService } from '@core/services/alert-notification.service';
import { LoggingService } from '@core/services/logging.service';
import { SafeStorageService } from '@core/services/safe-storage.service';
import {
  AlertNotificationBridgeService,
  AlertNotificationData,
} from '@core/services/alert-notification-bridge.service';
import { CompanyService } from '@core/services/company.service';
import { AuthService } from '@core/services/auth.service';
import { NinjaCreditsService, CreditSummary } from '@core/services/ninja-credits.service';

interface Notification {
  uid: string;
  level: string;
  type: string;
  title: string;
  message: string;
  timestamp?: Date;
  action?: {
    label: string;
    callback: () => void;
  };
}

interface UserCompany {
  uid: string;
  name: string;
  tier: 'free' | 'trial' | 'premium' | 'expired';
  isCurrent: boolean;
}

// Convert ToastNotification to header Notification format
interface HeaderNotification extends Notification {
  duration?: number;
}

@Component({
  selector: 'app-page-header',
  templateUrl: './page-header.component.html',
  styleUrl: './page-header.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PageHeaderComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private userService = inject(UserService);
  private renderer = inject(Renderer2);
  private toastNotificationService = inject(ToastNotificationService);
  private scanNotificationIntegration = inject(
    ScanNotificationIntegrationService,
  );
  private scanStatusMonitor = inject(ScanStatusMonitorService);
  private fileService = inject(FileService);
  private alertNotificationService = inject(AlertNotificationService);
  private loggingService = inject(LoggingService);
  private safeStorage = inject(SafeStorageService);
  private alertBridgeService = inject(AlertNotificationBridgeService);
  private signalrRealtime = inject(SignalrRealtimeService);
  private companyService = inject(CompanyService);
  private authService = inject(AuthService);
  public ninjaCreditsService = inject(NinjaCreditsService);

  // showProfileMenu = false;
  showNotyMenu = false;
  sideBarvisible = false;
  userModel: User | null = null;
  mspAccount?: TenableChildAccount | null;
  /** True until the Tenable account lookup has produced a model or settled without one. */
  licenseLookupPending = true;
  private accountModelRequested = false;
  userRoles: any = roleData.roles;

  // Multi-tenant company switcher properties
  userCompanies: UserCompany[] = [];
  isLoadingCompanies = false;
  isSwitchingCompany = false;

  isDev = !environment.production;

  // Replace hardcoded notifications with dynamic ones from services
  notifications: HeaderNotification[] = [];
  private notificationSubscriptions: Subscription[] = [];

  // Ninja Coins widget state
  creditSummary: CreditSummary | null = null;

  remainingTime: { hours: number; minutes: number; seconds: number } = {
    hours: 0,
    minutes: 0,
    seconds: 0,
  };
  countdownSubscription: Subscription | null = null;

  @ViewChild('notyButton') notyButton!: ElementRef;
  @ViewChild('notyMenu') notyMenu!: ElementRef;
  @ViewChild('profileBtn') profileBtn!: ElementRef;
  @ViewChild('profileMenu') profileMenu!: ElementRef;

  constructor() {
    // https://stackoverflow.com/questions/51150422/how-to-detect-click-outside-of-an-elem`ent-in-angular
    this.renderer.listen('window', 'click', (e: Event) => {
      // // Profile Links
      // if (!this.profileMenu.nativeElement.contains(e.target)
      // && !this.profileBtn.nativeElement.contains(e.target)
      // ) {
      //   this.showProfileMenu = false;
      // }

      // Notification Buttons
      if (this.notyButton && this.notyMenu) {
        if (
          !this.notyButton.nativeElement.contains(e.target) &&
          !this.notyMenu.nativeElement.contains(e.target)
        ) {
          this.showNotyMenu = false;
        }
      }
    });
    this.themeService.getSidebarIsVisable().subscribe((value: boolean) => {
      this.sideBarvisible = value;
    });
  }

  ngOnInit(): void {
    const creditSub = this.ninjaCreditsService.creditSummary$.subscribe((summary) => {
      this.creditSummary = summary;
    });
    this.notificationSubscriptions.push(creditSub);

    this.userService.getUserModel().subscribe((user) => {
      if (user && user.uid) {
        this.userModel = user;
        this.initDarkMode();
        // Initialize notification system once user is loaded
        this.initializeNotificationSystem();
        // Load user companies for tenant switcher
        this.loadUserCompanies();
        // Make sure the license/account model gets published for this tab
        this.requestAccountModel(user);
      }
    });
    this.themeService.getAccountModel().subscribe((mspAccount) => {
      if (mspAccount) {
        this.mspAccount = mspAccount;
        this.licenseLookupPending = false;

        // Update is_eval value.. If account expires longer than 30 days then its not an eval account.
        // If the account expires within 30 days, we consider it an eval account. and update the expiration to only 7 days.
        const expirationDate = new Date(
          mspAccount.license_expiration_date * 1000,
        ); // convert seconds to ms
        const currentDate = new Date();
        const daysRemaining = Math.ceil(
          (expirationDate.getTime() - currentDate.getTime()) /
            (1000 * 3600 * 24),
        );

        if (daysRemaining > 30) {
          mspAccount.is_eval = false;
        } else {
          mspAccount.is_eval = true;
          // store expiration as Unix timestamp (seconds)
          // set expiration to 7 days from the account creation date (fallback to now if unavailable)
          const sevenDaysSec = 7 * 24 * 60 * 60;

          let creationEpochSec: number | undefined;
          const createdAtNum =
            (mspAccount as any).created_at ??
            (mspAccount as any).creation_date ??
            (mspAccount as any).account_created_at;

          if (typeof createdAtNum === 'number') {
            // normalize ms vs. seconds
            creationEpochSec =
              createdAtNum > 9999999999
                ? Math.floor(createdAtNum / 1000)
                : createdAtNum;
          } else if ((mspAccount as any).createdAt) {
            const parsed = Date.parse((mspAccount as any).createdAt as string);
            if (!Number.isNaN(parsed)) {
              creationEpochSec = Math.floor(parsed / 1000);
            }
          }

          if (!creationEpochSec) {
            creationEpochSec = Math.floor(currentDate.getTime() / 1000);
          }

          mspAccount.license_expiration_date = creationEpochSec + sevenDaysSec;
        }

        if (this.isExpiringSoon(mspAccount.license_expiration_date)) {
          // Reset any existing countdown to avoid duplicate intervals
          if (this.countdownSubscription) {
            this.countdownSubscription.unsubscribe();
            this.countdownSubscription = null;
          }
          this.countdownSubscription = interval(1000).subscribe(() => {
            if (this.mspAccount)
              this.updateCountdown(this.mspAccount.license_expiration_date);
          });
        }
      } else {
        // Model was cleared (sign-out / company switch): stop showing the
        // previous tenant's license details.
        this.mspAccount = null;
        if (this.countdownSubscription) {
          this.countdownSubscription.unsubscribe();
          this.countdownSubscription = null;
        }
      }
    });
    this.getAccountDetails();
  }

  ngOnDestroy(): void {
    // Clean up the subscription when the component is destroyed
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
    }

    // Clean up notification subscriptions
    this.notificationSubscriptions.forEach((sub) => {
      if (sub) {
        sub.unsubscribe();
      }
    });
    this.notificationSubscriptions = [];

    // Stop scan notification system
    this.scanNotificationIntegration.stop();
    void this.signalrRealtime.stop();
  }

  showProfileMenu: boolean = false;

  toggleMenu() {
    this.showProfileMenu = !this.showProfileMenu;
    if (this.showProfileMenu) {
      this.showMenu();
    } else {
      this.hideMenu();
    }
  }

  showMenu() {
    this.showProfileMenu = true;
    // this.showProfileMenu = !this.showProfileMenu;
  }

  hideMenu() {
    this.showProfileMenu = false;
  }

  /**
   * Closes the profile menu when clicking outside of it.
   * @param event MouseEvent to detect the click location.
   */
  @HostListener('document:click', ['$event'])
  closeMenuOnOutsideClick(event: MouseEvent) {
    // Check if click target is outside the profile button and menu
    if (
      this.showProfileMenu &&
      this.profileBtn &&
      !this.profileBtn.nativeElement.contains(event.target)
    ) {
      this.showProfileMenu = false;
    }
  }

  showNotifications() {
    this.showNotyMenu = !this.showNotyMenu;
  }

  /**
   * Closes the notifications menu
   */
  closeNotifications() {
    this.showNotyMenu = false;
  }

  /**
   * Handles notification click - closes menu and navigates if needed
   * @param notification The notification that was clicked
   */
  onNotificationClick(notification: HeaderNotification) {
    this.closeNotifications();

    // Execute action if available
    if (notification.action && notification.action.callback) {
      notification.action.callback();
    } else {
      // Default navigation for notifications without specific actions
      if (notification.type === 'Scan') {
        // Try to extract scan ID from uid for scan notifications
        const scanIdMatch = notification.uid.match(/scan-(.+)-\d+/);
        if (scanIdMatch) {
          this.navigateToScan(scanIdMatch[1]);
        } else {
          this.router.navigate(['/dashboard/scans']);
        }
      } else {
        // Generic notification navigation
        this.router.navigate(['/dashboard/notifications']);
      }
    }
  }

  /**
   * * Removes a notification from the list of notifications.
   * @param notification The notification object to be removed.
   * TODO * Add logic to remove the notification from the server or database if needed.
   */
  removeNotification(notification: HeaderNotification) {
    // Remove from local array
    this.notifications = this.notifications.filter(
      (n) => n.uid !== notification.uid,
    );

    // If it's a toast notification, also remove it from the toast service
    if (notification.type === 'System') {
      this.toastNotificationService.removeNotification(notification.uid);
    }

    // Close the menu if no notifications remain
    if (this.notifications.length === 0) {
      this.closeNotifications();
    }
  }

  toggleSidebar() {
    this.themeService.toggleSidebarVisibility();
  }

  /**
   * Returns the credit burn percentage (0–100) for lightweight header display.
   */
  getCreditBurnPercent(): number {
    if (!this.creditSummary) return 0;
    return this.ninjaCreditsService.getBurnPercent(this.creditSummary);
  }

  /**
   * Returns Bootstrap color token for usage severity.
   */
  getCreditColorClass(): string {
    if (!this.creditSummary) return 'success';
    return this.ninjaCreditsService.getBalanceColorClass(this.creditSummary);
  }

  /**
   * Asks AuthService to publish the current company's Tenable account when this
   * tab has none cached, then clears the header's loading state once the lookup
   * has settled so the license spinner cannot run indefinitely (#729).
   * @param user Signed-in user whose current company is looked up.
   */
  private requestAccountModel(user: User): void {
    if (this.accountModelRequested) {
      return;
    }
    this.accountModelRequested = true;
    this.authService
      .ensureAccountModel(user)
      .catch(() => undefined)
      .finally(() => {
        this.licenseLookupPending = false;
      });
  }

  getAccountDetails() {
    const mspAccountData = this.safeStorage.getSessionItem('accountDetails');
    if (mspAccountData) {
      this.mspAccount = new TenableChildAccount(JSON.parse(mspAccountData));
      this.themeService.setAccountModel(this.mspAccount);
    }
  }

  // Initialize dark mode BehaviorSubject with a default value of true
  dark: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  /**
   * Initializes the dark mode setting for the application.
   * If a user profile is available, it sets the dark mode according to the user's theme preference.
   * Defaults to dark mode if no preference is specified.
   */
  initDarkMode() {
    // Get the dark mode setting from the user profile, if available
    if (this.userModel && this.userModel.themeColor) {
      this.dark.next(this.userModel.themeColor === 'dark');
    }

    // Set the theme based on the current value of dark mode
    document.body.setAttribute(
      'data-bs-theme',
      this.dark.value ? 'dark' : 'light',
    );
    const themeClass = this.dark.value ? 'dark' : 'light';
    document.body.classList.remove('dark', 'light');
    document.body.classList.add(themeClass);
    this.themeService.setTheme(themeClass);
  }

  /**
   * Toggles the dark mode setting for the application.
   * This function switches the current theme between dark and light modes,
   * updates the body's `data-bs-theme` attribute, and calls the user service to save the new preference.
   */
  toggleDarkMode() {
    // Toggle dark mode value
    const newDarkModeValue = !this.dark.value;
    this.dark.next(newDarkModeValue);

    // Update theme attribute on the body
    document.body.setAttribute(
      'data-bs-theme',
      newDarkModeValue ? 'dark' : 'light',
    );

    // Update the user profile theme setting if applicable
    this.userService.updateUserTheme(newDarkModeValue ? 'dark' : 'light');
    this.themeService.setTheme(newDarkModeValue ? 'dark' : 'light');
  }

  getClassByLevel(level: string) {
    switch (level) {
      case 'Alert':
        return 'alert-danger';
      case 'Warning':
        return 'alert-warning';
      case 'Notice':
        return 'alert-primary';
      case 'Info':
        return 'alert-info';
      case 'Reminder':
        return 'alert-secondary';
      default:
        return 'alert-primary';
    }
  }

  /**
   * Determine if the license is expiring within the next 24 hours.
   * @param expirationDate Unix timestamp in seconds
   */
  isExpiringSoon(expirationDate: number): boolean {
    const currentTimeMs = Date.now();
    const expirationMs = expirationDate * 1000; // expirationDate is in seconds
    const remainingMs = expirationMs - currentTimeMs;
    return remainingMs <= 24 * 60 * 60 * 1000; // 24 hours in ms
  }

  /**
   * Get the remaining time until expiration (hours and minutes).
   * @param expirationDate Unix timestamp in seconds
   */
  getRemainingTime(expirationDate: number) {
    const currentTimeMs = Date.now();
    const remainingMs = expirationDate * 1000 - currentTimeMs;

    // Calculate hours and minutes from ms
    const hours = Math.floor(remainingMs / (60 * 60 * 1000));
    const minutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000));

    return { hours, minutes };
  }

  /**
   * Update the countdown timer values (h/m/s) until expiration.
   * @param expirationDate Unix timestamp in seconds
   */
  updateCountdown(expirationDate: number) {
    const currentTimeMs = Date.now();
    const remainingMs = expirationDate * 1000 - currentTimeMs;

    // Stop the countdown when time runs out
    if (remainingMs <= 0) {
      this.remainingTime = { hours: 0, minutes: 0, seconds: 0 };
      return;
    }

    // Calculate hours, minutes, and seconds from ms
    const hours = Math.floor(remainingMs / (60 * 60 * 1000));
    const minutes = Math.floor((remainingMs % (60 * 60 * 1000)) / (60 * 1000));
    const seconds = Math.floor((remainingMs % (60 * 1000)) / 1000);

    this.remainingTime = { hours, minutes, seconds };
  }

  /**
   * Get the role name based on the role ID.
   * @param roleId Role ID to search for in the user roles list.
   * @returns The role name if found, or an empty string if not found.
   */
  getRoleName(roleId: string) {
    const role = this.userRoles.find((role: any) => role.id === roleId);
    return role ? role.name : '';
  }

  openSearch() {
  }

  /**
   * Initialize the notification system and start monitoring
   */
  private async initializeNotificationSystem(): Promise<void> {
    try {
      // Subscribe to toast notifications from the service
      const toastSub = this.toastNotificationService
        .getNotifications()
        .subscribe((toastNotifications: ToastNotification[]) => {
          this.updateNotificationsFromToast(toastNotifications);
        });
      this.notificationSubscriptions.push(toastSub);

      // Subscribe to scan status changes for scan completion notifications
      const scanSub = this.scanStatusMonitor
        .getScanStatusChanges()
        .subscribe((statusChanges: ScanStatusChange[]) => {
          this.handleScanStatusChanges(statusChanges);
        });
      this.notificationSubscriptions.push(scanSub);

      // Subscribe to alert notifications from the bridge service
      const alertSub = this.alertBridgeService
        .getAlertNotifications()
        .subscribe((alerts: AlertNotificationData[]) => {
          this.handleAlertNotifications(alerts);
        });
      this.notificationSubscriptions.push(alertSub);

      // Initialize alert notification service for the current user
      if (this.userModel?.uid) {
        await this.alertNotificationService.initialize(this.userModel.uid);
      }

      // Start the scan notification integration system
      await this.scanNotificationIntegration.start({
        enableDesktopNotifications: true,
        enableInAppNotifications: true,
        autoNavigateToCompletedScans: false,
        monitoringIntervalMinutes: 1,
        enableBulkNotifications: true,
      });

      // Real-time server pushes (Azure SignalR via func-scanninja-alerts).
      // Not awaited: a SignalR outage must not block the header.
      void this.signalrRealtime.start();
    } catch (error) {
      console.error('Failed to initialize notification system:', error);
    }
  }

  /**
   * Convert ToastNotifications to HeaderNotifications for display
   */
  private updateNotificationsFromToast(
    toastNotifications: ToastNotification[],
  ): void {
    const headerNotifications: HeaderNotification[] = toastNotifications.map(
      (toast) => ({
        uid: toast.id,
        level: this.mapToastTypeToLevel(toast.type),
        type: 'System',
        title: toast.title,
        message: this.stripHtmlTags(toast.message),
        timestamp: toast.timestamp,
        action: toast.action,
        duration: toast.duration,
      }),
    );

    // Merge with existing notifications (scan notifications)
    const scanNotifications = this.notifications.filter(
      (n) => n.type === 'Scan',
    );
    this.notifications = [...headerNotifications, ...scanNotifications];
  }

  /**
   * Handle scan status changes and add them as notifications
   */
  private handleScanStatusChanges(statusChanges: ScanStatusChange[]): void {
    const scanNotifications: HeaderNotification[] = statusChanges
      .filter((change) => change.currentStatus === 'completed')
      .map((change) => ({
        uid: `scan-${change.scanId}-${Date.now()}`,
        level: 'Info',
        type: 'Scan',
        title: 'Scan Completed',
        message: `${change.name} has finished scanning and results are ready to review.`,
        timestamp: change.timestamp,
        action: {
          label: 'View Results',
          callback: () => this.navigateToScan(change.scanId),
        },
      }));

    if (scanNotifications.length > 0) {
      // Add scan notifications to existing ones
      const systemNotifications = this.notifications.filter(
        (n) => n.type === 'System',
      );
      this.notifications = [...systemNotifications, ...scanNotifications];
    }
  }

  /**
   * Map ToastNotification type to header notification level
   */
  private mapToastTypeToLevel(
    type: 'success' | 'error' | 'warning' | 'info',
  ): string {
    switch (type) {
      case 'success':
        return 'Info';
      case 'error':
        return 'Alert';
      case 'warning':
        return 'Warning';
      case 'info':
      default:
        return 'Notice';
    }
  }

  /**
   * Strip HTML tags from notification messages for display
   */
  private stripHtmlTags(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  /**
   * Navigate to scan results page
   */
  private navigateToScan(scanId: string): void {
    this.router.navigate(['/dashboard/scans/view', scanId]);
    this.closeNotifications();
  }

  /**
   * Get formatted time ago string for notification timestamp
   */
  getTimeAgo(timestamp?: Date): string {
    if (!timestamp) {
      return 'now';
    }

    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) {
      return 'now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes}m`;
    } else if (diffHours < 24) {
      return `${diffHours}h`;
    } else {
      return `${diffDays}d`;
    }
  }

  /**
   * Clear all notifications
   */
  clearAllNotifications(): void {
    // Clear toast notifications
    this.toastNotificationService.clearAll();

    // Clear local notifications
    this.notifications = [];
    this.closeNotifications();
  }

  /**
   * Get notification count for display
   */
  getNotificationCount(): number {
    return this.notifications.length;
  }

  /**
   * Check if there are any unread notifications
   */
  hasUnreadNotifications(): boolean {
    return this.notifications.length > 0;
  }

  /**
   * Handle alert notifications from the bridge service
   */
  private handleAlertNotifications(alerts: AlertNotificationData[]): void {
    // Convert the first few alerts to header notifications
    const alertNotifications: HeaderNotification[] = alerts
      .slice(0, 5)
      .map((alert) => ({
        uid: alert.uid,
        level: this.mapAlertSeverityToLevel(alert.severity),
        type: 'Alert',
        title: alert.title,
        message: alert.message,
        timestamp: alert.timestamp,
        action: {
          label: 'View Details',
          callback: () => {
            // Navigate based on alert type
            if (
              alert.type.includes('SCAN') ||
              alert.type.includes('VULNERABILITY')
            ) {
              this.router.navigate(['/dashboard/scans']);
            } else if (alert.type.includes('TICKET')) {
              this.router.navigate(['/dashboard/support']);
            } else if (alert.type.includes('COMPLIANCE')) {
              this.router.navigate(['/dashboard/soc2-compliance']);
            } else {
              this.router.navigate(['/dashboard/notifications']);
            }
            this.closeNotifications();
          },
        },
      }));

    // Merge with existing notifications
    const systemNotifications = this.notifications.filter(
      (n) => n.type === 'System',
    );
    const scanNotifications = this.notifications.filter(
      (n) => n.type === 'Scan',
    );
    this.notifications = [
      ...alertNotifications,
      ...systemNotifications,
      ...scanNotifications,
    ];

    // Keep only last 10 notifications
    if (this.notifications.length > 10) {
      this.notifications = this.notifications.slice(0, 10);
    }
  }

  /**
   * Add an alert notification to the header display
   * This method can be called by other components or services to add alert notifications
   */
  addAlertNotification(alert: {
    type: string;
    severity: string;
    title: string;
    message: string;
    metadata?: any;
  }): void {
    // Generate cryptographically secure random ID (6 bytes = 12 hex chars)
    const randomBytes = new Uint8Array(6);
    window.crypto.getRandomValues(randomBytes);
    const securePart = Array.from(randomBytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');

    const alertNotification: HeaderNotification = {
      uid: `alert-${Date.now()}-${securePart}`,
      level: this.mapAlertSeverityToLevel(alert.severity),
      type: 'Alert',
      title: alert.title,
      message: alert.message,
      timestamp: new Date(),
      action: {
        label: 'View Details',
        callback: () => {
          // Navigate based on alert type
          if (
            alert.type.includes('SCAN') ||
            alert.type.includes('VULNERABILITY')
          ) {
            this.router.navigate(['/dashboard/scans']);
          } else if (alert.type.includes('TICKET')) {
            this.router.navigate(['/dashboard/support']);
          } else if (alert.type.includes('COMPLIANCE')) {
            this.router.navigate(['/dashboard/soc2-compliance']);
          } else {
            this.router.navigate(['/dashboard/notifications']);
          }
          this.closeNotifications();
        },
      },
    };

    // Add to the notifications array
    const systemNotifications = this.notifications.filter(
      (n) => n.type === 'System',
    );
    const scanNotifications = this.notifications.filter(
      (n) => n.type === 'Scan',
    );
    this.notifications = [
      alertNotification,
      ...systemNotifications,
      ...scanNotifications,
    ];

    // Keep only last 10 notifications
    if (this.notifications.length > 10) {
      this.notifications = this.notifications.slice(0, 10);
    }

    this.loggingService.logInfo('🔔 Alert notification added to header', {
      type: alert.type,
      severity: alert.severity,
      title: alert.title,
    });
  }

  /**
   * Map alert severity to header notification level
   */
  private mapAlertSeverityToLevel(severity: string): string {
    switch (severity.toLowerCase()) {
      case 'critical':
      case 'p1':
        return 'Alert';
      case 'high':
      case 'p2':
        return 'Warning';
      case 'medium':
      case 'p3':
        return 'Notice';
      case 'low':
      case 'p4':
        return 'Info';
      case 'info':
      default:
        return 'Info';
    }
  }

  /**
   * TrackBy function for notifications to improve performance
   */
  trackByNotificationId(
    index: number,
    notification: HeaderNotification,
  ): string {
    return notification.uid;
  }

  /**
   * Get a safe URL for the user's photo, handling encoding issues
   * @returns string - Safe photo URL or default icon
   */
  getSafePhotoURL(): string {
    return this.fileService.cleanPhotoURL(this.userModel?.photoURL);
  }

  /**
   * Load all companies the user has access to
   * @returns Promise<void> that resolves when companies are loaded, or immediately if user has no companies
   */
  async loadUserCompanies(): Promise<void> {
    if (!this.userModel || !this.userModel.companies) {
      this.userCompanies = [];
      return;
    }

    // Handle both array and object format for companies
    // Sometimes Firebase stores arrays as objects with numeric keys
    let currentCompanyIds: string[] = [];
    if (Array.isArray(this.userModel.companies)) {
      currentCompanyIds = this.userModel.companies;
    } else if (typeof this.userModel.companies === 'object') {
      // Convert object to array: {"0": "id1", "1": "id2"} -> ["id1", "id2"]
      currentCompanyIds = Object.values(this.userModel.companies);
    }

    if (currentCompanyIds.length === 0) {
      this.userCompanies = [];
      return;
    }

    this.isLoadingCompanies = true;

    try {
      const companyPromises = currentCompanyIds.map(async (currentCompanyId) => {
        try {
          const company = await this.companyService.getCompany(currentCompanyId);
          if (company) {
            // Validate tier value to ensure type safety
            const validTiers: Array<'free' | 'trial' | 'premium' | 'expired'> =
              ['free', 'trial', 'premium', 'expired'];
            const tier = validTiers.includes(company.tier as any)
              ? company.tier
              : 'free';

            return {
              uid: company.uid,
              name: company.name || 'Unnamed Company',
              tier: tier as 'free' | 'trial' | 'premium' | 'expired',
              isCurrent: currentCompanyId === this.userModel?.currentCompanyId,
            } as UserCompany;
          }
          return null;
        } catch (error) {
          this.loggingService.logError(
            `Failed to load company ${currentCompanyId}:`,
            error,
          );
          return null;
        }
      });

      const companies = await Promise.all(companyPromises);
      this.userCompanies = companies.filter(
        (c): c is UserCompany => c !== null,
      );
    } catch (error) {
      this.loggingService.logError('Failed to load user companies:', error);
      this.userCompanies = [];
    } finally {
      this.isLoadingCompanies = false;
    }
  }

  /**
   * Switch to a different company
   */
  async switchToCompany(currentCompanyId: string): Promise<void> {
    if (!this.userModel) {
      return;
    }

    if (this.isSwitchingCompany) {
      return;
    }

    // Don't switch if already on this company
    if (currentCompanyId === this.userModel.currentCompanyId) {
      return;
    }

    this.isSwitchingCompany = true;

    try {
      const success = await this.userService.switchCompany(currentCompanyId);

      if (success) {
        // Drop the previous tenant's cached Tenable account/expiry state so the
        // wait page re-validates for the new company (#729).
        this.authService.clearAccountCache();

        // Republish the session from Firestore before routing. switchCompany
        // writes currentCompanyId and updates UserService's own subject, but
        // AuthService still held the user it hydrated at sign-in — and the wait
        // page answers from that. Routing without this rebuilt the dashboard on
        // the PREVIOUS company, which only a full browser refresh cleared,
        // because a refresh is the one thing that empties the in-memory copy
        // (#569).
        const refreshed = await this.authService.refreshHydratedUser();
        if (refreshed) {
          this.userModel = refreshed;
        }

        this.toastNotificationService.showSuccess(
          'Company Switched',
          'Switching to the selected company...',
          2000,
        );

        // Redirect to logging-in page to rebuild data and cache
        setTimeout(() => {
          this.router.navigate(['/logging-in']);
        }, 2000);
      } else {
        this.toastNotificationService.showError(
          'Switch Failed',
          'Failed to switch company. Please try again.',
          5000,
        );
      }
    } catch (error) {
      this.loggingService.logError('Error switching company:', error);
      this.toastNotificationService.showError(
        'Error',
        'An error occurred while switching companies.',
        5000,
      );
    } finally {
      this.isSwitchingCompany = false;
    }
  }

  /**
   * Get display text for company tier
   */
  getTierDisplayName(tier: string): string {
    const tierMap: Record<string, string> = {
      // 'free' is the stored value for a company with no plan (#847).
      free: 'No Plan',
      trial: 'Trial',
      premium: 'Premium',
      expired: 'Expired',
    };
    if (!tierMap[tier]) {
      this.loggingService.logWarning(`Unknown tier: ${tier}`);
    }
    return tierMap[tier] || 'No Plan';
  }

  /**
   * Get tier badge class for styling
   */
  getTierBadgeClass(tier: string): string {
    const classMap: Record<string, string> = {
      free: 'tier-free',
      trial: 'tier-trial',
      premium: 'tier-premium',
      expired: 'tier-expired',
    };
    return classMap[tier] || 'tier-free';
  }

  /**
   * Check if user has multiple companies
   */
  hasMultipleCompanies(): boolean {
    return this.userCompanies.length > 1;
  }
}
